<template>
  <div :class="$style.tab">
    <div :class="$style.headerRow">
      <div>
        <h3 :class="$style.title">A/B Test targeting</h3>
        <p :class="$style.subtitle">
          Control whether this offer is
          <strong>included</strong>
          or
          <strong>excluded</strong>
          for specific A/B tests and their cohorts. An offer can either be excluded from selected cohorts, or restricted
          to only appear in selected cohorts — not both.
        </p>
      </div>
      <div :class="$style.modeControl">
        <div :class="$style.toggle" role="group" aria-label="A/B test targeting mode">
          <button
            type="button"
            :class="[$style.toggleBtn, isExcluded ? $style.toggleActiveExclude : '']"
            :disabled="saving"
            @click="setMode('EXCLUDE')"
          >
            <span :class="[$style.dot, isExcluded ? $style.dotExclude : $style.dotInactive]"></span>
            Exclude
          </button>
          <button
            type="button"
            :class="[$style.toggleBtn, !isExcluded ? $style.toggleActiveInclude : '']"
            :disabled="saving"
            @click="setMode('INCLUDE')"
          >
            <span :class="[$style.dot, !isExcluded ? $style.dotInclude : $style.dotInactive]"></span>
            Include
          </button>
        </div>
        <VdIcon v-if="saving" store="fas" name="fa-spinner" class="fa-spin" custom-size="14px" color="#6b7280" />
      </div>
    </div>

    <div :class="[$style.modeBanner, isExcluded ? $style.modeBannerExclude : $style.modeBannerInclude]">
      <VdIcon
        store="fas"
        :name="isExcluded ? 'ban' : 'circle-check'"
        custom-size="16px"
        :color="isExcluded ? '#b91c1c' : '#15803d'"
      />
      <span v-if="isExcluded">
        This offer will be
        <strong>excluded</strong>
        from the A/B tests &amp; cohorts listed below. It shows everywhere else.
      </span>
      <span v-else>
        This offer will
        <strong>only</strong>
        appear for the A/B tests &amp; cohorts listed below. It is hidden everywhere else.
      </span>
    </div>

    <div v-if="isLinked" :class="$style.testList">
      <div v-for="test in resolvedTests" :key="test.abTestId" :class="$style.testCard">
        <div :class="$style.testCardHeader">
          <router-link :to="linkTo(test.abTestId)" :class="$style.testName">{{ test.name }}</router-link>
          <span :class="[$style.tag, isExcluded ? $style.excludeTag : $style.includeTag]">
            {{ isExcluded ? "Excluded" : "Included" }}
          </span>
        </div>
        <!-- One line per cohort, its treatments as segment chips (mirrors the bulk dialog's summary). -->
        <div v-if="test.cohorts.length" :class="$style.cohortList">
          <div v-for="cohort in test.cohorts" :key="cohort.cohortId" :class="$style.cohortLine">
            <span :class="$style.cohortName">{{ cohort.cohortName }}</span>
            <span v-for="segmentName in cohort.segmentNames" :key="segmentName" :class="$style.treatmentChip">
              {{ segmentName }}
            </span>
          </div>
        </div>
      </div>

      <div v-if="unresolvedCount > 0" :class="$style.unresolved">
        + {{ unresolvedCount }} unpublished {{ unresolvedCount === 1 ? "test" : "tests" }}
      </div>

      <div v-if="!isExcluded && unresolvedCount > 0" :class="$style.deadLinkWarning">
        <VdIcon store="fas" name="triangle-exclamation" custom-size="14px" color="#b45309" />
        <span>
          {{ unresolvedCount }} {{ unresolvedCount === 1 ? "link references a test" : "links reference tests" }} that
          {{ unresolvedCount === 1 ? "doesn't" : "don't" }} exist in this version — in
          <strong>Include</strong>
          mode those links serve no one until the {{ unresolvedCount === 1 ? "test is" : "tests are" }} published here.
        </span>
      </div>
    </div>

    <div v-else :class="$style.empty">
      <template v-if="isExcluded">
        Not excluded from any A/B test — this offer is served to everyone in its segments.
      </template>
      <template v-else>
        Not linked to any A/B test. In
        <strong>Include</strong>
        mode with no links, this offer is served to no one.
      </template>
    </div>

    <Dialog
      :visible="confirmVisible"
      modal
      :draggable="false"
      :style="{ width: '440px' }"
      header="Switch targeting mode?"
      @update:visible="confirmVisible = false"
    >
      <p :class="$style.confirmText">
        Switching to
        <strong>{{ pendingMode === "EXCLUDE" ? "Exclude" : "Include" }}</strong>
        clears all {{ linkCount }} A/B-test {{ linkCount === 1 ? "link" : "links" }} on this offer — a link set authored
        for one mode cannot be reused in the other. You will need to link the offer again afterwards.
      </p>
      <template #footer>
        <div :class="$style.confirmFooter">
          <Button label="Cancel" severity="secondary" outlined @click="confirmVisible = false" />
          <Button label="Switch and clear links" severity="danger" @click="confirmModeSwitch" />
        </div>
      </template>
    </Dialog>
  </div>
</template>

<script setup>
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";
import VdIcon from "@/components/VdIcon/VdIcon.vue";
import { useAbtestCohortSelectInputQuery } from "@/graphql/generated-types";

import { useSegments } from "../useSegments";

const props = defineProps({
  // Legacy single-link scalar; fallback for offers not yet on the multi-link shape.
  abTestId: {
    type: String,
    default: null,
  },
  abtestLinkMode: {
    type: String,
    default: null,
  },
  abtestLinks: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(["update-abtest-link-mode"]);

const route = useRoute();
const { gameId, versionId } = route.params;

const isExcluded = computed(() => props.abtestLinkMode === "EXCLUDE");

// Mode is editable here (links are not). Persisting is done by the parent; we flag `saving` optimistically
// and clear it when the persisted mode prop reflects the change (or after a safety timeout on failure).
const saving = ref(false);

watch(
  () => props.abtestLinkMode,
  () => {
    saving.value = false;
  }
);

const effectiveLinks = computed(() => {
  if (props.abtestLinks && props.abtestLinks.length > 0) {
    return props.abtestLinks;
  }
  if (props.abTestId) {
    return [{ abTestId: props.abTestId, cohortId: null }];
  }
  return [];
});

const isLinked = computed(() => effectiveLinks.value.length > 0);
const linkCount = computed(() => effectiveLinks.value.length);

// A mode FLIP on a linked offer clears ALL its links backend-side (a link set authored for one mode is
// meaningless in the other), so it requires an explicit confirmation first.
const confirmVisible = ref(false);
const pendingMode = ref(null);

const persistMode = (mode) => {
  saving.value = true;
  emit("update-abtest-link-mode", mode);
  window.setTimeout(() => {
    saving.value = false;
  }, 5000);
};

const setMode = (mode) => {
  if (saving.value) {
    return;
  }
  const current = props.abtestLinkMode === "INCLUDE" ? "INCLUDE" : "EXCLUDE";
  if (mode === current) {
    return;
  }
  if (isLinked.value) {
    pendingMode.value = mode;
    confirmVisible.value = true;
    return;
  }
  persistMode(mode);
};

const confirmModeSwitch = () => {
  confirmVisible.value = false;
  if (pendingMode.value) {
    persistMode(pendingMode.value);
    pendingMode.value = null;
  }
};

// Resolve abtest/cohort/treatment names within the current version. Links whose abtest isn't present in
// this version (e.g. not yet published to it) can't be named and are summarized as "+ N unpublished".
const { result } = useAbtestCohortSelectInputQuery({ gameId, versionId });
const { segments: allSegments } = useSegments(gameId);
const segmentNameById = computed(() => new Map((allSegments.value ?? []).map((segment) => [segment.id, segment.name])));

const abtestsById = computed(() => {
  const map = new Map();
  const abtests = result.value?.game?.version?.abtests ?? [];
  abtests.forEach((abtest) => {
    map.set(abtest.id, {
      name: abtest.name,
      cohorts: new Map((abtest.cohorts ?? []).map((cohort) => [cohort.id, cohort.name])),
      // treatment id -> its segment id, to label each link's treatment by its segment name.
      treatmentSegments: new Map(
        (abtest.cohorts ?? []).flatMap((cohort) =>
          (cohort.treatments ?? []).map((treatment) => [treatment.id, treatment.segmentId])
        )
      ),
    });
  });
  return map;
});

// Group links by abtest so a test linked through several treatments renders one card with one chip per
// (cohort, treatment) link.
const groupedByTest = computed(() => {
  const groups = new Map();
  effectiveLinks.value.forEach((link) => {
    if (!groups.has(link.abTestId)) {
      groups.set(link.abTestId, []);
    }
    groups.get(link.abTestId).push(link);
  });
  return groups;
});

const resolvedTests = computed(() =>
  [...groupedByTest.value.entries()]
    .map(([abTestId, links]) => {
      const abtest = abtestsById.value.get(abTestId);
      if (!abtest) {
        return null;
      }
      // One chip per COHORT, listing the treatments (by segment name) the offer is linked through — an
      // offer may hold several links into the same cohort, one per treatment.
      const cohortGroups = new Map();
      links
        .filter((link) => link.cohortId)
        .forEach((link) => {
          if (!cohortGroups.has(link.cohortId)) {
            cohortGroups.set(link.cohortId, {
              cohortId: link.cohortId,
              cohortName: abtest.cohorts.get(link.cohortId) ?? link.cohortId,
              segmentNames: [],
            });
          }
          const segmentId = link.treatmentId ? abtest.treatmentSegments.get(link.treatmentId) : null;
          if (segmentId) {
            cohortGroups.get(link.cohortId).segmentNames.push(segmentNameById.value.get(segmentId) ?? segmentId);
          }
        });
      return {
        abTestId,
        name: abtest.name,
        cohorts: [...cohortGroups.values()],
      };
    })
    .filter(Boolean)
);

const unresolvedCount = computed(() => groupedByTest.value.size - resolvedTests.value.length);

function linkTo(abTestId) {
  return `/games/${gameId}/${versionId}/abtests/${abTestId}/edit/cohorts`;
}
</script>

<style module>
.tab {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 4px;
}

.headerRow {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}

.title {
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 600;
}

.subtitle {
  margin: 0;
  color: var(--vd-color-text-light-2, #6b7280);
  font-size: 14px;
  max-width: 480px;
}

.modeControl {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.toggle {
  display: inline-flex;
  gap: 4px;
  padding: 4px;
  border-radius: 10px;
  background-color: var(--vd-color-neutral-light-10, #f3f4f6);
}

.toggleBtn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--vd-color-text-light-2, #6b7280);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.toggleBtn:disabled {
  cursor: default;
}

.toggleActiveInclude {
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  color: #15803d;
}

.toggleActiveExclude {
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  color: #b91c1c;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #9ca3af;
}

.dotInclude {
  background: #16a34a;
}

.dotExclude {
  background: #dc2626;
}

.dotInactive {
  background: #9ca3af;
}

.modeBanner {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.45;
}

.modeBannerInclude {
  background-color: #dcfce7;
  color: #15803d;
}

.modeBannerExclude {
  background-color: #fee2e2;
  color: #b91c1c;
}

.testList {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.testCard {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  border: 1px solid var(--vd-color-neutral-light-8, #e5e7eb);
  border-radius: 12px;
}

.testCardHeader {
  display: flex;
  align-items: center;
  gap: 10px;
}

.testName {
  font-size: 15px;
  font-weight: 600;
  color: inherit;
  text-decoration: none;
}

.testName:hover {
  text-decoration: underline;
}

.tag {
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
}

.includeTag {
  background-color: #dcfce7;
  color: #15803d;
}

.excludeTag {
  background-color: #fee2e2;
  color: #b91c1c;
}

.cohortList {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.cohortLine {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.cohortName {
  font-size: 13px;
  margin-right: 4px;
}

.treatmentChip {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 999px;
  background-color: #e0e7ff;
  color: #3730a3;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.unresolved {
  color: var(--vd-color-text-light-2, #6b7280);
  font-size: 13px;
  font-style: italic;
}

.deadLinkWarning {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 9px 12px;
  border-radius: 8px;
  background-color: #fef3c7;
  color: #92400e;
  font-size: 13px;
  line-height: 1.45;
}

.confirmText {
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
}

.confirmFooter {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}

.empty {
  color: var(--vd-color-text-light-2, #6b7280);
  font-size: 14px;
}
</style>
